import React, { useEffect, useState } from 'react';
import { connect, ResolveThunks } from 'react-redux';
import { ApiSanctionAlert, SanctionAlertsPage } from '../api';
import { sanctionAlertsApi, sanctionAlertsBulkApi } from '../apis';
import ScreeningEntityDetails from './ScreeningEntityDetails';
import FormattedDateTime from '../common/datetime/FormattedDateTime';
import { AppState } from '../store/store';
import Table from '../table/Table';
import './RelatedAlertsStatusModal.scss';
import UserEventTracker from '../tracking/userEventTracker';
import ScreeningMatchesPopover from './ScreeningMatchesPopover';
import { getValueFromEntityByFieldKey } from './helper';
import { systemAlertStatusCodes } from './alert-status/alertStatusesConstants';
import { InternalApiScreeningAlertStatus } from '../api-internal';
import { updateScreeningAlertSuccess } from './screeningReducer';
import { useQueryAttributeMappings } from '../schema/mappings/mappingsActions';
import DataLoadingErrorFallback from '../common/errors/DataLoadingErrorFallback';
import { SalvProgressCircle, SalvButton, SalvModal } from '@salvapp/ui-components';

export interface RelatedAlertsStatusModalProps {
  sanctionAlertId: number;
  note: string;
  status: InternalApiScreeningAlertStatus;
  onComplete?: () => void;
  triggerLegacyAlertUpdate?: boolean;
}

export const RelatedAlertsStatusModal = ({
  sanctionAlertId,
  note,
  status,
  onComplete,
  updateScreeningAlertSuccess,
  triggerLegacyAlertUpdate = true,
}: Props) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [page, setPage] = useState(0);
  const pageSize = 10;
  const [alertsPage, setAlertsPage] = useState<SanctionAlertsPage>({
    number: page,
    size: pageSize,
    total: 0,
    content: [],
  });
  useEffect(() => {
    if (!status.isGoodlistable) {
      triggerLegacyAlertUpdate && updateScreeningAlertSuccess();
      onComplete && onComplete();
      return;
    }
    sanctionAlertsApi
      .findAllRelatedAlertsByAlertIdV2({
        sanctionAlertId,
        status: systemAlertStatusCodes.new,
        page: page,
        size: pageSize,
      })
      .then((alertsPage) => {
        if (alertsPage.total && alertsPage.total > 0) {
          setAlertsPage(alertsPage);
          setShowModal(true);
        } else {
          triggerLegacyAlertUpdate && updateScreeningAlertSuccess();
          onComplete && onComplete();
        }
      });
  }, [sanctionAlertId, status, onComplete, page, pageSize]);

  const updateAlertsStatuses = async () => {
    setIsSubmitting(true);
    await sanctionAlertsBulkApi.updateScreeningRelatedAlertsStatuses({
      screeningAlertId: sanctionAlertId,
      apiRelatedAlertBulkStatus: {
        note: {
          content: note,
        },
        fromStatus: systemAlertStatusCodes.new,
        toStatus: status.code,
      },
    });
    UserEventTracker.trackUserEvent('RELATED_SCREENING_ALERT_STATUS_CHANGE', {
      alertId: sanctionAlertId,
      status,
      count: alertsPage.total,
    });
    relatedAlertsResolved();
  };

  const cancelChange = () => {
    relatedAlertsResolved();
  };

  const relatedAlertsResolved = () => {
    triggerLegacyAlertUpdate && updateScreeningAlertSuccess();
    setShowModal(false);
    setIsSubmitting(false);
    onComplete && onComplete();
  };

  const {
    data: attributeMappings,
    isPending: isAttributeMappingsPending,
    isError: isAttributeMappingsError,
  } = useQueryAttributeMappings();

  if (isAttributeMappingsError) {
    return <DataLoadingErrorFallback message={'Could not load Attribute Mappings'} />;
  } else if (isAttributeMappingsPending) {
    return <SalvProgressCircle ariaLabel={'Loading Attribute Mappings'} />;
  }

  const renderAlerts = () => {
    return alertsPage.content.map((alert: ApiSanctionAlert) => (
      <tr key={alert.id}>
        <td>{alert.id}</td>
        <td>
          <ScreeningEntityDetails entity={alert.entity} mappings={attributeMappings?.map} />
        </td>
        <td>
          <FormattedDateTime date={alert.createdTime} showTime />
        </td>
        <td className='highlight-bold-em'>
          <ScreeningMatchesPopover
            originalInput={getValueFromEntityByFieldKey(alert.fieldKey, alert.entity)}
            hits={alert.matches || []}
            status={alert.status}
          />
        </td>
      </tr>
    ));
  };

  return (
    <SalvModal show={showModal} onHide={() => relatedAlertsResolved()} data-testid='modal'>
      <SalvModal.Header closeButton>Bulk set alert statuses</SalvModal.Header>

      <SalvModal.Body>
        <p>
          There are {alertsPage.total} more new sanction alert
          {alertsPage.total && alertsPage.total > 1 ? 's' : ''} including the same counterparty.
        </p>
        <Table
          responsive={false}
          items={alertsPage.content}
          page={page}
          size={pageSize}
          total={alertsPage.total}
          fetchPage={setPage}
          header={() => (
            <tr>
              <th>Alert Id</th>
              <th>Object</th>
              <th>Alert time</th>
              <th>Matches</th>
            </tr>
          )}
          body={renderAlerts}
        />
        <p>
          Change the alert status to{' '}
          <span className='new-status'>
            <b>{status.textName}</b> • {note}
          </span>{' '}
          {alertsPage.total > 1 ? 'for all' : ''}?
        </p>
      </SalvModal.Body>
      <SalvModal.Footer>
        <SalvButton data-testid='close' variant='muted' onClick={cancelChange} label='Cancel' />
        <SalvButton
          data-testid='submit'
          disabled={isSubmitting}
          onClick={updateAlertsStatuses}
          variant='primary'
          label={alertsPage.total > 1 ? 'Change all' : 'Change'}
        />
      </SalvModal.Footer>
    </SalvModal>
  );
};

const mapDispatchToProps = {
  updateScreeningAlertSuccess,
};

const mapStateToProps = (state: AppState) => {
  const { schema } = state;

  return {
    ...schema,
  };
};

type Props = ResolveThunks<typeof mapDispatchToProps> &
  ReturnType<typeof mapStateToProps> &
  RelatedAlertsStatusModalProps;

export default connect(mapStateToProps, mapDispatchToProps)(RelatedAlertsStatusModal);
